/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useState, useEffect } from 'react';
import { withTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { useFormContext } from 'react-hook-form';
import {
    InternalGrid,
    ButtonGroup,
    Pagination,
    Text,
    Link,
} from '@paygreen/paygreen-ui';
import { useAPIError, useMode } from '../../../context';
import { SimpleActionButton, ValidateButton } from '../../../components';
import { useDebounce } from '../../../utils/hooks';
import {
    getAllMonetaryRatioCategories,
    getAllEmissionsFactors,
    getAllMonetaryRatios,
} from '../service';
import CategoryProductForm from './CategoryProductForm';
import CategoryProductIntro from './CategoryProductIntro';

const CategoryProduct = ({
    t,
    selectedProduct,
    defaultValues,
    matchWithSector = true,
    groupedSubmit,
    isGrouped = false,
    groupedCancel,
    isCategory,
    cancelCloseModal,
}) => {
    const { addError } = useAPIError();
    const { mode } = useMode();
    const { setValue, getValues } = useFormContext();

    const [productsData, setProductsData] = useState();
    const [productCategories, setProductCategories] = useState([]);
    const [searchCategory, setSearchCategory] = useState(0);
    const [searchName, setSearchName] = useState('');
    const [pageIndex, setPageIndex] = useState(0);
    const [pageCount, setPageCount] = useState(1);
    const [isMatchingSector, setIsMatchingSector] = useState(matchWithSector);
    const [isLoading, setIsLoading] = useState(false);

    // we apply debounce to limit API requests when typing name in input
    const debouncedSearchName = useDebounce(searchName, 600);

    const [tempSelectedProduct, setTempSelectedProduct] = useState({
        productExternalReference: defaultValues?.productExternalReference,
        productName: defaultValues?.productName,
        emissionExternalId: defaultValues?.emissionExternalId,
        monetaryRatioMatch: defaultValues?.monetaryRatioMatch,
        emissionFactorSource: defaultValues?.emissionFactorSource,
        emissionFactorName: '',
        monetaryRatioName: '',
        emissionFactorValue:
            defaultValues?.emissionFactorValue +
            defaultValues?.emissionFactorUnit,
        monetaryRatioValue:
            defaultValues?.monetaryRatioValue +
            defaultValues?.monetaryRatioUnit,
        documentation: '',
        source: '',
    });

    const chosenProductRef =
        getValues().productExternalReference ||
        defaultValues.productExternalReference;
    const chosenProductName =
        getValues().productName || defaultValues.productName;

    useEffect(() => {
        let isMounted = true;
        // to get product categories tree built by API
        if (!isMatchingSector) {
            getAllMonetaryRatios()
                .then((res) => {
                    if (isMounted) {
                        setProductCategories(res.data._embedded.monetary_ratio);
                    }
                })
                .catch((error) => {
                    addError(
                        error?.response?.data.detail,
                        error?.response?.data.status,
                    );
                });
        } else {
            getAllMonetaryRatioCategories()
                .then((res) => {
                    if (isMounted) {
                        setProductCategories(
                            res.data._embedded.monetary_ratio_category,
                        );
                    }
                })
                .catch((error) => {
                    addError(
                        error?.response?.data.detail,
                        error?.response?.data.status,
                    );
                });
        }

        return () => {
            isMounted = false;
        };
    }, [addError, mode, isMatchingSector]);

    useEffect(() => {
        setTempSelectedProduct({
            productExternalReference: defaultValues?.productExternalReference,
            productName: defaultValues?.productName,
            emissionExternalId: defaultValues?.emissionExternalId,
            monetaryRatioMatch: defaultValues?.monetaryRatioMatch,
            emissionFactorSource: defaultValues?.emissionFactorSource,
            emissionFactorName: '',
            monetaryRatioName: '',
            emissionFactorValue:
                defaultValues?.emissionFactorValue +
                defaultValues?.emissionFactorUnit,
            monetaryRatioValue:
                defaultValues?.monetaryRatioValue +
                defaultValues?.monetaryRatioUnit,
            documentation: '',
            source: '',
        });
    }, [defaultValues]);

    useEffect(() => {
        setPageIndex(0);
        setSearchCategory('');
        setSearchName('');
    }, [isMatchingSector]);

    useEffect(() => {
        let isMounted = true;
        if (isMounted) {
            setIsLoading(true);
        }

        const params = {
            limit: 6,
            page: pageIndex + 1,
            search: debouncedSearchName || null,
            category: isMatchingSector ? null : searchCategory || null,
            categoryId: isMatchingSector ? searchCategory || null : null,
        };

        const retrieveEntitiesFunc = !isMatchingSector
            ? getAllEmissionsFactors
            : getAllMonetaryRatios;

        // to get all emission factors or monetary ratio from API
        retrieveEntitiesFunc(params, mode)
            .then((res) => {
                if (isMounted) {
                    setProductsData(
                        !isMatchingSector
                            ? res.data._embedded.emissionFactor
                            : res.data._embedded.monetary_ratio,
                    );
                    setPageCount(res.data.page_count);
                }
            })
            .catch((error) => {
                if (error?.response?.data.status === 404) {
                    if (isMounted) {
                        setProductsData([]);
                        setPageCount(1);
                    }
                }
                if (error?.response?.data.status === 409) {
                    if (isMounted) {
                        setProductsData([]);
                        setPageCount(1);
                    }
                } else {
                    addError(
                        error?.response?.data.detail,
                        error?.response?.data.status,
                    );
                }
            })
            .finally(() => {
                isMounted && setIsLoading(false);
            });

        return () => {
            isMounted = false;
        };
    }, [
        searchCategory,
        debouncedSearchName,
        addError,
        pageIndex,
        mode,
        isMatchingSector,
    ]);

    // cancel choice and reset values in form only if no defaultValues to link product
    const cancelChosenProduct = () => {
        if (!selectedProduct) {
            for (const [key] of Object.entries(tempSelectedProduct).slice(2)) {
                setValue(key, '');
            }
        }
        if (groupedCancel) {
            groupedCancel();
        }
        if (cancelCloseModal) {
            cancelCloseModal();
        }
    };

    // confirm choice and update all values in form
    const confirmChosenProduct = () => {
        for (const [key, value] of Object.entries(tempSelectedProduct)) {
            setValue(key, value);
        }
        groupedSubmit();
    };

    // unlink product by resetting all product values associated with emissionExternalId and also defaultValues
    const unlinkProduct = () => {
        for (const [key] of Object.entries(tempSelectedProduct).slice(2)) {
            setValue(key, null);
        }
        groupedSubmit();
    };

    return (
        <InternalGrid
            displayType="grid"
            gridGap="md"
            alignItems="stretch"
            justifyItems="stretch"
            gridTemplateColumns="1fr"
        >
            {!isCategory && !isGrouped && (
                <CategoryProductIntro
                    unlinkProduct={unlinkProduct}
                    chosenProductRef={chosenProductRef}
                    chosenProductName={chosenProductName}
                    selectedProduct={selectedProduct}
                    selectedProductEntity={defaultValues}
                />
            )}

            <InternalGrid childrenMarginBottom="sm">
                <Text colorPallet="wab" textSize="xs">
                    {isMatchingSector &&
                        !isCategory &&
                        t('matching.addManuallyForm.sectorIntro')}
                    {isMatchingSector && !!isCategory && (
                        <>
                            {t('matching.addManuallyForm.sectorIntroCategory')}{' '}
                            <Text htmlTag="strong" textSize="xs">
                                {defaultValues?.categoryName}
                            </Text>
                        </>
                    )}
                    {!isMatchingSector &&
                        t('matching.addManuallyForm.equivalentIntro')}
                </Text>
            </InternalGrid>

            <CategoryProductForm
                searchName={searchName}
                setSearchName={setSearchName}
                setPageIndex={setPageIndex}
                searchCategory={searchCategory}
                setSearchCategory={setSearchCategory}
                setTempSelectedProduct={setTempSelectedProduct}
                tempSelectedProduct={tempSelectedProduct}
                isLoading={isLoading}
                productsData={productsData}
                productCategories={productCategories}
                isMatchingSector={isMatchingSector}
            />

            <InternalGrid
                alignItems="center"
                childrenMarginBottom="sm"
                justifyContent="center"
            >
                <Pagination
                    pageCount={pageCount}
                    pageIndex={pageIndex}
                    gotoPage={setPageIndex}
                />
            </InternalGrid>

            <ButtonGroup marginTop="xs">
                <SimpleActionButton
                    buttonSize="md"
                    icon="ArrowRoundTopLeftIcon"
                    onClick={() => cancelChosenProduct()}
                >
                    {t('form.button.cancel')}
                </SimpleActionButton>

                <ValidateButton
                    buttonSize="md"
                    type="button"
                    onClick={() => confirmChosenProduct()}
                >
                    {t('form.button.save')}
                </ValidateButton>
            </ButtonGroup>

            {!defaultValues.emissionExternalId && (
                <>
                    <Text colorPallet="wab" textSize="xs" marginBottom="sm">
                        {isMatchingSector &&
                            !isCategory &&
                            t('matching.addManuallyForm.noSectorFound') + ' '}
                        {!isMatchingSector &&
                            !isCategory &&
                            t('matching.addManuallyForm.noEquivalentFound') +
                                ' '}
                        {isMatchingSector &&
                            !!isCategory &&
                            t(
                                'matching.addManuallyForm.noSectorFoundCategory',
                            ) + ' '}
                        {!isMatchingSector &&
                            !!isCategory &&
                            t(
                                'matching.addManuallyForm.noEquivalentFoundCategory',
                            ) + ' '}

                        <a>
                            <Link
                                colorPallet="theme"
                                hasUnderline={false}
                                onClick={() =>
                                    setIsMatchingSector(!isMatchingSector)
                                }
                            >
                                {isMatchingSector &&
                                    t('matching.addManuallyForm.equivalent')}
                                {!isMatchingSector &&
                                    t('matching.addManuallyForm.sector')}
                            </Link>
                        </a>
                        {'.'}
                    </Text>
                </>
            )}
        </InternalGrid>
    );
};

CategoryProduct.propTypes = {
    selectedProduct: PropTypes.string,
    cancelCloseModal: PropTypes.func,
    defaultValues: PropTypes.object,
    matchWithSector: PropTypes.bool,
    isCategory: PropTypes.bool,
};

export default withTranslation('common')(CategoryProduct);
